import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { AppShell } from '@/components/AppShell'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { Login } from '@/pages/Login'
import { Handoff } from '@/pages/Handoff'
import { ForgotPassword } from '@/pages/ForgotPassword'
import { ResetPassword } from '@/pages/ResetPassword'
import { Dashboard } from '@/pages/Dashboard'
import { Normalization } from '@/pages/Normalization'
import { SellerPortal } from '@/pages/SellerPortal'
import { BuyerPortal } from '@/pages/BuyerPortal'
import { AdminDashboard } from '@/pages/AdminDashboard'
import { RequestAccess } from '@/pages/RequestAccess'
import { GettingStarted } from '@/pages/GettingStarted'
import { ReportGenerator } from '@/pages/ReportGenerator'
import { DealDetail } from '@/pages/DealDetail'
import { TaxDocumentReview } from '@/pages/TaxDocumentReview'
import { QoEFSApprovalPage } from '@/pages/QoEFSApprovalPage'
import EntityStructurePage from '@/pages/EntityStructurePage'
import { AccessRequestsPage } from '@/pages/admin/AccessRequestsPage'
import { MonitorPage } from '@/pages/admin/MonitorPage'
import { UsersPage } from '@/pages/admin/UsersPage'
import { EntitlementsPage } from '@/pages/admin/EntitlementsPage'
import { BrandRegistrationPage } from '@/pages/BrandRegistrationPage'
import { FirmProfilePage } from '@/pages/FirmProfilePage'
import { AccountPage } from '@/pages/AccountPage'
import { BrandRegistrationsPage } from '@/pages/admin/BrandRegistrationsPage'
import CompliancePage from '@/pages/admin/CompliancePage'
import ScopeConfigPage from '@/pages/admin/ScopeConfig'
import EngagementCreate from '@/pages/EngagementCreate'
import { ScopeCatalogue } from '@/pages/ScopeCatalogue'
import { QuoteBuilder } from '@/pages/QuoteBuilder'
import { ValuationGate } from '@/components/valuation/ValuationGate'
import { ProductGate } from '@/components/ProductGate'
import ValuationProfileSelect from '@/pages/valuation/ValuationProfileSelect'
import ValuationCompany from '@/pages/valuation/ValuationCompany'
import ValuationFinancials from '@/pages/valuation/ValuationFinancials'
import ValuationNormalize from '@/pages/valuation/ValuationNormalize'
import ValuationIntake from '@/pages/valuation/ValuationIntake'
import ValuationTuning from '@/pages/valuation/ValuationTuning'
import ValuationOutput from '@/pages/valuation/ValuationOutput'
import ValuationSignOff from '@/pages/valuation/ValuationSignOff'
import { LetterGenerator } from '@/pages/attest/LetterGenerator'
import { LetterHistory } from '@/pages/attest/LetterHistory'
import { ATTEST_HISTORY_ENABLED } from '@/components/attest/flags'
import { PrivacyPolicyPage } from '@/pages/legal/PrivacyPolicyPage'
import { DataRetentionPage } from '@/pages/legal/DataRetentionPage'
import { TermsOfServicePage } from '@/pages/legal/TermsOfServicePage'
import { Hub } from '@/pages/Hub'
import { useAuth } from '@/contexts/AuthContext'
import { landingPathForUser } from '@/lib/roleLanding'

// RBAC-1: the nav map moved to @/lib/productNav so it can be resolved against
// entitlements without importing the router (AppShell reads it from '@/router',
// so leaving it here would make that a value-import cycle). Re-exported because
// callers and tests name '@/router' as its home.
export { NAV_ITEMS_BY_ROLE, navItemsFor } from '@/lib/productNav'
export type { NavItem } from '@/lib/productNav'

function DefaultRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()
  if (isLoading) return null
  // '*' also renders this: preserve the unmatched deep link so a logged-out user
  // who pasted /deals/abc gets back to it after signing in.
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />
  // Entitlement decides, with the role map as the fallback when /me has not
  // reported products. This replaced a valuation-only special case: that role
  // now resolves to its single openable product and lands on /valuation the
  // same way every other single-product account does.
  return <Navigate to={landingPathForUser(user)} replace />
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  // PLAT-ADMIN-HANDOFF-1: public; signs nobody in until the person clicks Continue.
  { path: '/handoff', element: <Handoff /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/request-access', element: <RequestAccess /> },
  { path: '/seller', element: <SellerPortal /> },
  { path: '/buyer', element: <BuyerPortal /> },
  { path: '/privacy', element: <PrivacyPolicyPage /> },
  { path: '/data-retention', element: <DataRetentionPage /> },
  { path: '/terms', element: <TermsOfServicePage /> },
  { path: '/', element: <DefaultRedirect /> },
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      // RBAC-1 build item 5: the launcher for multi-product accounts. Reachable
      // by URL for everyone — a single-product user is auto-routed past it, not
      // barred from it, and it is the one page that can honestly say "you have
      // nothing open yet" instead of bouncing to a 403.
      { path: '/hub', element: <Hub /> },
      { path: '/dashboard', element: <Dashboard /> },
      // USER-PROFILE-1: no `allowedRoles` on purpose. Every authenticated user
      // has their own account, so gating this by role could only ever lock
      // someone out of their own name — unlike /firm-profile below, which is
      // one firm's legal identity and is correctly role-gated.
      { path: '/account', element: <AccountPage /> },
      { path: '/normalization', element: <Normalization /> },
      // AUTHZ-PRODUCT-ROUTES-1: the page's read-only content (preflight
      // findings, dashboard-style summaries) is qoe:read, so the floor for
      // reaching it at all is that same role set. The narrower qoe:manage
      // gate on the actual release actions (generate/PDF) is enforced
      // in-page by ReportGenerator's own role check, not here — an analyst
      // still has legitimate business viewing this page.
      {
        path: '/reports',
        element: (
          <ProtectedRoute allowedRoles={['super_admin', 'cpa', 'analyst', 'firm']}>
            <ReportGenerator />
          </ProtectedRoute>
        ),
      },
      { path: '/getting-started', element: <GettingStarted /> },
      { path: '/deals/:dealId', element: <DealDetail /> },
      { path: '/deals/:dealId/entities', element: <EntityStructurePage /> },
      { path: '/deals/:dealId/tax', element: <TaxDocumentReview /> },
      // FINDINGS-DOMAIN-1 S4. Both are readable by any signed-in role: what we
      // assess and what it costs are the answers a prospect is owed, and the
      // quote endpoint creates nothing.
      { path: '/scope', element: <ScopeCatalogue /> },
      { path: '/quote', element: <QuoteBuilder /> },
      {
        path: '/engagements/new',
        element: (
          <ProtectedRoute allowedRoles={['super_admin', 'cpa', 'tester']}>
            <EngagementCreate />
          </ProtectedRoute>
        ),
      },
      // ATTEST-UI-1. Issuing a verification letter is a CPA act — the same roles
      // that may create an engagement, and no wider. The route is gated even
      // though every guardrail is re-enforced server-side.
      //
      // ProductGate nests INSIDE the role check on purpose: ProtectedRoute's
      // allowedRoles is layer 2 (capability — "may a cpa do this at all"),
      // ProductGate is layer 1 (entitlement — "does this firm hold Attest").
      // Without it, a cpa/tester at a firm lacking Attest passed the role gate
      // clean and only discovered the refusal as raw text out of LetterGenerator's
      // first API call (REFUSAL-COPY-1 D3).
      {
        path: '/attest/letters',
        element: (
          <ProtectedRoute allowedRoles={['super_admin', 'cpa', 'tester']}>
            <ProductGate product="attest" label="Verification Letters">
              <LetterGenerator />
            </ProductGate>
          </ProtectedRoute>
        ),
      },
      ...(ATTEST_HISTORY_ENABLED
        ? [{
            path: '/attest/history',
            element: (
              <ProtectedRoute allowedRoles={['super_admin', 'cpa', 'tester']}>
                <ProductGate product="attest" label="Verification Letters">
                  <LetterHistory />
                </ProductGate>
              </ProtectedRoute>
            ),
          }]
        : []),
      { path: '/qoe/financial-statements/:fsId/review', element: <QoEFSApprovalPage /> },
      // Business Valuator — a gated VIEW inside this SPA, not a second app.
      // `ValuationGate` (not ProtectedRoute's allowedRoles) does the deny, so an
      // unentitled user is told why instead of being bounced to /dashboard.
      { path: '/valuation', element: <ValuationGate><ValuationProfileSelect /></ValuationGate> },
      { path: '/valuation/:valuationId/company', element: <ValuationGate><ValuationCompany /></ValuationGate> },
      { path: '/valuation/:valuationId/financials', element: <ValuationGate><ValuationFinancials /></ValuationGate> },
      { path: '/valuation/:valuationId/adjust', element: <ValuationGate><ValuationNormalize /></ValuationGate> },
      { path: '/valuation/:valuationId/intake', element: <ValuationGate><ValuationIntake /></ValuationGate> },
      { path: '/valuation/:valuationId/tuning', element: <ValuationGate><ValuationTuning /></ValuationGate> },
      { path: '/valuation/:valuationId/output', element: <ValuationGate><ValuationOutput /></ValuationGate> },
      { path: '/valuation/:valuationId/signoff', element: <ValuationGate><ValuationSignOff /></ValuationGate> },
      {
        path: '/brand-registration',
        element: (
          <ProtectedRoute allowedRoles={['super_admin', 'cpa', 'analyst', 'firm', 'tester']}>
            <BrandRegistrationPage />
          </ProtectedRoute>
        ),
      },
      // CPA-ONBOARDING-1. Separate from /brand-registration: that page is the
      // white-label look and feel (slug, colors, domain); this one is the firm's
      // legal identity (legal name, licensed CPA, license no/state, prefix).
      {
        path: '/firm-profile',
        element: (
          <ProtectedRoute allowedRoles={['super_admin', 'cpa', 'firm', 'tester']}>
            <FirmProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/brand-registrations',
        element: (
          <ProtectedRoute allowedRoles={['super_admin']}>
            <BrandRegistrationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin',
        element: (
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/access-requests',
        element: (
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AccessRequestsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/users',
        element: (
          <ProtectedRoute allowedRoles={['super_admin']}>
            <UsersPage />
          </ProtectedRoute>
        ),
      },
      {
        // ENTITLEMENT-LIFECYCLE-1 Phase 1. Role-gated only, never product-gated:
        // deciding who holds a product cannot itself sit behind holding one.
        path: '/admin/entitlements',
        element: (
          <ProtectedRoute allowedRoles={['super_admin']}>
            <EntitlementsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/monitor',
        element: (
          <ProtectedRoute allowedRoles={['super_admin']}>
            <MonitorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/scope-config',
        element: (
          <ProtectedRoute allowedRoles={['super_admin']}>
            <ScopeConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/compliance',
        element: (
          <ProtectedRoute allowedRoles={['super_admin', 'cpa', 'tester']}>
            <CompliancePage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  { path: '*', element: <DefaultRedirect /> },
])
